var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useTheme } from "styled-components";
import { Box } from "../Box";
import { Flex } from "../Flex";
import { useWizardStep } from "./hooks";
import { WizardAlign } from "./types";
import { WizardStep, WizardStepStatusType } from "./WizardStep";
export const WizardSection = ({ isVisible, children }) => isVisible && (React.createElement(motion.div, { animate: { opacity: 1 }, exit: { opacity: 0 }, initial: { opacity: 0 }, style: { width: "100%" } }, children));
export const calculateWizardStatus = (currentStep, stepNumber) => {
    if (currentStep === stepNumber) {
        return WizardStepStatusType.CURRENT;
    }
    if (currentStep > stepNumber) {
        return WizardStepStatusType.COMPLETED;
    }
    return WizardStepStatusType.PENDING;
};
export const invertFlexAlign = (align) => align === WizardAlign.Horizontal ? WizardAlign.Vertical : WizardAlign.Horizontal;
export const WizardContext = React.createContext({});
export const WizardPage = (_a) => {
    var { children } = _a, props = __rest(_a, ["children"]);
    return React.createElement("div", Object.assign({}, props), children);
};
export const Wizard = ({ align = "Horizontal", allowStepsNav = false, initialStep = 0, cancelButton, stepLabel, showLabel = true, wizardBodyProps, children, }) => {
    const Alignment = WizardAlign[align];
    const InvertedAlign = invertFlexAlign(Alignment);
    const { currentStep, toNextStep, toPreviousStep, toStep } = useWizardStep(initialStep, children.length);
    const value = useMemo(() => ({
        toNextStep,
        toPreviousStep,
        toStep,
    }), [toNextStep, toPreviousStep, toStep]);
    const { colors: { wizardColors }, } = useTheme();
    const footerStyles = {
        backgroundColor: wizardColors.footerBackground,
        justifyContent: "space-between",
        padding: "11px",
    };
    return (React.createElement(Box, { "data-testid": "wizardContainer" },
        React.createElement(WizardContext.Provider, { value: value },
            React.createElement(Flex, Object.assign({ "data-testid": "wizard", flexDirection: InvertedAlign }, wizardBodyProps),
                React.createElement(Flex, { flexDirection: Alignment, sx: { width: "100%" } },
                    React.createElement(Flex, { "data-testid": "wizardSteps", flexDirection: InvertedAlign, flexGrow: 0 }, children.map(({ props: { labelText } }, index) => (React.createElement(WizardStep, { key: `step-${index}`, stepNumber: index, stepLabel: stepLabel, status: calculateWizardStatus(currentStep, index), onClick: allowStepsNav ? () => toStep(index) : undefined, showLabel: showLabel, showSeparation: index < children.length - 1, align: InvertedAlign }, labelText)))),
                    React.createElement(Flex, { "data-testid": "wizardSections", flexDirection: Alignment, flexGrow: 1, key: "wizardSections", mb: "18px" },
                        React.createElement(Box, { border: "1px solid", borderColor: wizardColors.border, borderRadius: "4px", width: "100%", p: 3, ml: 4 },
                            React.createElement(AnimatePresence, null, children.map(({ props: { children: content } }, index) => (React.createElement(WizardSection, { key: `section-${index}`, isVisible: currentStep === index }, content)))))))),
            React.createElement(Flex, { "data-testid": "wizardFooter", sx: footerStyles },
                React.createElement(Flex, null, cancelButton),
                React.createElement(Flex, { style: { gap: "10px" } }, children.map(({ props: { buttons } }, index) => index === currentStep && buttons ? buttons({ key: `buttons-${index}`, toNextStep, toPreviousStep }) : null))))));
};
